// app/frontend/pages/PrivateLessonPages/UserLessonIndex.tsx
import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Title,
  Text,
  MultiSelect,
  Pagination,
  Badge,
  MantineProvider,
  ActionIcon,
  Drawer,
  Button,
  Stack,
  Divider,
  Group,
} from '@mantine/core';
import { Head } from '@inertiajs/react';
import { IconAdjustmentsHorizontal } from '@tabler/icons-react';
import { DatePickerInput } from '@mantine/dates';

interface Booking {
  id: number;
  status: string;
  status_text: string;
  date: string; // e.g., "2023-03-29"
  time: string; // e.g., "02:00 PM"
  lesson_type: string;
  artist: {
    id: number;
    name: string;
    slug: string;
    avatar_url?: string;
  };
  event?: {
    id: number;
    name: string;
    image_url?: string;
  } | null;
  booking_url: string;
}

interface BookingsIndexProps {
  bookings: Booking[];
  totalBookings: number;
  // Options for filters should have value and label fields
  artists: { value: string; label: string }[];
  events: { value: string; label: string }[];
  statuses: { value: string; label: string }[];
  // We'll assume user_booking includes the current user's username:
  user_booking: {
    user_username: string;
  };
}

export default function BookingsIndex({
  bookings,
  totalBookings,
  artists,
  events,
  statuses,
  user_booking,
}: BookingsIndexProps) {
  const [filteredBookings, setFilteredBookings] = useState<Booking[]>(bookings);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(16);

  // Filter states
  const [selectedArtists, setSelectedArtists] = useState<string[]>([]);
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const [filterDrawerOpened, setFilterDrawerOpened] = useState(false);

  // Format filter options to ensure that each option's value is a string
  const formattedArtists = artists.map((item) => ({
    value: String(item.value),
    label: item.label,
  }));
  const formattedEvents = events.map((item) => ({
    value: String(item.value),
    label: item.label,
  }));
  const formattedStatuses = statuses.map((item) => ({
    value: String(item.value),
    label: item.label,
  }));

  // Calculate active filter count (for badge display)
  const activeFilterCount =
    selectedArtists.length + selectedEvents.length + selectedStatuses.length + (startDate && endDate ? 1 : 0);

  // Auto-select filters based on the first booking's artist and event (if any) when the page loads
  useEffect(() => {
    if (bookings.length > 0) {
      // Auto-select the artist from the first booking
      setSelectedArtists([bookings[0].artist.id.toString()]);
      // Auto-select the event if present in the first booking
      if (bookings[0].event) {
        setSelectedEvents([bookings[0].event.id.toString()]);
      }
    }
  }, [bookings]);

  // Update filtered bookings in real time as filters change
  useEffect(() => {
    // Start with bookings excluding declined events
    let result = bookings.filter((booking) => booking.status !== 'declined');

    // Filter by artist ID (convert booking.artist.id to string)
    if (selectedArtists.length > 0) {
      result = result.filter((booking) => selectedArtists.includes(booking.artist.id.toString()));
    }

    // Filter by event ID (only if booking.event exists)
    if (selectedEvents.length > 0) {
      result = result.filter((booking) => booking.event && selectedEvents.includes(booking.event.id.toString()));
    }

    // Filter by status (convert booking.status to string for filtering)
    if (selectedStatuses.length > 0) {
      result = result.filter((booking) => selectedStatuses.includes(String(booking.status)));
    }

    // Filter by date range
    if (startDate && endDate) {
      result = result.filter((booking) => {
        const bookingDate = new Date(booking.date);
        return bookingDate >= startDate && bookingDate <= endDate;
      });
    }

    // Sort bookings chronologically (by date and time)
    result.sort((a, b) => {
      const aDate = new Date(a.date + ' ' + a.time);
      const bDate = new Date(b.date + ' ' + b.time);
      return aDate.getTime() - bDate.getTime();
    });

    setFilteredBookings(result);
    setCurrentPage(1);
  }, [selectedArtists, selectedEvents, selectedStatuses, startDate, endDate, bookings]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredBookings.length / itemsPerPage);
  const paginatedBookings = filteredBookings.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Clear all filters
  const handleClearFilters = () => {
    setSelectedArtists([]);
    setSelectedEvents([]);
    setSelectedStatuses([]);
    setStartDate(null);
    setEndDate(null);
  };

  // Helper: map booking status to a Mantine color using your mapping:
  // '1' (approved) => Blue, '2' (pending) => Yellow, '3' (completed) => Green, '4' (declined) => Gray
  const getStatusColor = (status: any) => {
    // Convert status to string and trim it
    const s = String(status).trim();
    // Mapping object (adjust values as needed)
    const statusMapping: { [key: string]: string } = {
      '1': 'approved',
      '2': 'pending',
      '3': 'completed',
      '4': 'declined',
    };
    const mappedStatus = statusMapping[s] || 'unknown';
    console.log('Processed status:', s, 'mapped to:', mappedStatus);
    switch (mappedStatus) {
      case 'approved':
        return 'blue';
      case 'pending':
        return 'yellow';
      case 'completed':
        return 'green';
      default:
        return 'gray';
    }
  };

  return (
    <>
      <MantineProvider>
        <Head title="My Private Lesson Bookings" />

        <Container size="md" mt="md">
          {/* Header with title and filter icon */}
          <Box mb="xl">
            <Group justify="apart" align="center" style={{ marginBottom: '1rem', width: '100%' }}>
              <Title size="2rem" fw={700} className="text-white poppins">
                Your Private Lessons
              </Title>
              <Group align="center" style={{ position: 'relative' }}>
                <ActionIcon variant="light" size="xl" color="orange" onClick={() => setFilterDrawerOpened(true)}>
                  <IconAdjustmentsHorizontal />
                </ActionIcon>
                {activeFilterCount > 0 && (
                  <Badge
                    color="orange"
                    size="sm"
                    radius="xl"
                    style={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      transform: 'translate(50%, -50%)',
                    }}
                  >
                    {activeFilterCount}
                  </Badge>
                )}
              </Group>
            </Group>
          </Box>

          {/* Summary Text */}
          <Text mb="md" className="poppins" style={{ color: '#f2f2f2' }}>
            Currently showing {paginatedBookings.length} of {filteredBookings.length} private lessons.
          </Text>

          {/* Bookings Grid */}
          <Grid gutter="md">
            {paginatedBookings.map((booking) => (
              <Grid.Col span={12} key={booking.id}>
                <Box mb="md">
                  <Box
                    p="md"
                    style={{
                      backgroundColor: '#FEF5ED10',
                      borderRadius: '8px',
                      margin: '1rem 0',
                    }}
                  >
                    <Group align="center" style={{ width: '100%' }}>
                      <div>
                        <Group className="mb-3">
                          <Badge color={getStatusColor(booking.status)} size="lg">
                            {booking.status_text}
                          </Badge>
                          <Title size="lg" fw={700} c="#f2f2f2" className="poppins">
                            {booking.lesson_type}
                          </Title>
                        </Group>
                        <Text style={{ color: '#f2f2f2' }}>
                          {new Date(booking.date).toLocaleDateString()} at {booking.time} with {booking.artist.name}
                          {booking.event ? ` at ${booking.event.name}` : ''}
                        </Text>
                      </div>
                      {/* Instead of linking to the booking URL, navigate to the "my lessons" page */}
                      <Button
                        component="a"
                        variant="filled"
                        color="orange"
                        size="md"
                        className="poppins"
                        style={{ marginLeft: 'auto' }}
                      >
                        View Lessons
                      </Button>
                    </Group>
                  </Box>
                </Box>
              </Grid.Col>
            ))}
          </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination total={totalPages} value={currentPage} onChange={setCurrentPage} mt="xl" color="orange" />
          )}
        </Container>

        {/* Filter Drawer */}
        <Drawer
          opened={filterDrawerOpened}
          onClose={() => setFilterDrawerOpened(false)}
          position="right"
          size="md"
          withCloseButton
          styles={{
            root: { zIndex: 1000 },
            header: {
              backgroundColor: '#18181B',
              color: '#f2f2f2',
              borderBottom: '1px solid #333',
            },
            body: { backgroundColor: '#18181B', color: '#f2f2f2' },
            close: { color: '#f2f2f2' },
          }}
        >
          <Stack gap="sm" p="md" style={{ minHeight: '100vh' }}>
            <Text size="lg" fw={600} c="#f2f2f2">
              Filters
            </Text>
            <Divider mb="md" color="#333" />

            {/* Artist Filter */}
            <Box>
              <Text fw={500} mb="xs" c="#f2f2f2">
                Artist
              </Text>
              <MultiSelect
                data={formattedArtists}
                placeholder="Select artist(s)"
                value={selectedArtists}
                onChange={setSelectedArtists}
                clearable
                hidePickedOptions
                searchable
                variant="filled"
                size="md"
                style={{ width: '100%' }}
              />
            </Box>

            <Divider my="md" color="#333" />

            {/* Event Filter */}
            <Box>
              <Text fw={500} mb="xs" c="#f2f2f2">
                Event
              </Text>
              <MultiSelect
                data={formattedEvents}
                placeholder="Select event(s)"
                value={selectedEvents}
                onChange={setSelectedEvents}
                clearable
                hidePickedOptions
                searchable
                variant="filled"
                size="md"
                style={{ width: '100%' }}
              />
            </Box>

            <Divider my="md" color="#333" />

            {/* Status Filter */}
            <Box>
              <Text fw={500} mb="xs" c="#f2f2f2">
                Status
              </Text>
              <MultiSelect
                data={formattedStatuses}
                placeholder="Select status(es)"
                value={selectedStatuses}
                onChange={setSelectedStatuses}
                clearable
                hidePickedOptions
                searchable
                variant="filled"
                size="md"
                style={{ width: '100%' }}
              />
            </Box>

            <Divider my="md" color="#333" />

            {/* Date Range Filter */}
            <Box>
              <Text fw={500} mb="xs" c="#f2f2f2">
                Date Range
              </Text>
              <Group>
                <DatePickerInput
                  placeholder="Start date"
                  label="Start"
                  value={startDate}
                  onChange={setStartDate}
                  style={{ flex: 1 }}
                />
                <DatePickerInput
                  placeholder="End date"
                  label="End"
                  value={endDate}
                  onChange={setEndDate}
                  style={{ flex: 1 }}
                />
              </Group>
            </Box>

            <Divider my="md" color="#333" />

            {/* Action Buttons */}
            <Group justify="space-between" mt="lg">
              <Button
                variant="light"
                color="orange"
                size="md"
                onClick={handleClearFilters}
                disabled={activeFilterCount === 0}
              >
                Clear All Filters
              </Button>
              <Button
                variant="filled"
                color="orange"
                radius="md"
                size="md"
                onClick={() => setFilterDrawerOpened(false)}
              >
                Show {filteredBookings.length} Bookings
              </Button>
            </Group>
          </Stack>
        </Drawer>
      </MantineProvider>
    </>
  );
}
